/**
 * Application-layer message payload inside the MLS-encrypted frame.
 *
 * Three variants multiplex in a single wire type:
 *   - text: plain chat
 *   - attachment: encrypted-media reference
 *   - game: pluggable game move (see apps/web/src/games/engine.ts)
 *
 * Every payload is padded to a fixed bucket (256, 1 KiB, 4 KiB, 16 KiB,
 * 64 KiB) before MLS encryption so per-message ciphertext length doesn't
 * leak content. See docs/traffic-posture.md.
 */
import type { UserId } from '@chat/protocol';
import type { AttachmentReference } from './attachments.js';

export const PAYLOAD_BUCKETS: readonly number[] = [256, 1024, 4096, 16_384, 65_536] as const;

export interface GameMessage {
  readonly engineId: string;
  /** Monotonic per-conversation index. Index 0 starts a new game. */
  readonly moveIndex: number;
  /** Engine-specific move payload. */
  readonly move: unknown;
  /**
   * Must be present on `moveIndex === 0`. Lists the users who are
   * playing (everyone else in the conversation is a spectator).
   */
  readonly players?: readonly UserId[];
  /**
   * Sender's MLS identity userId. Authenticated by virtue of the MLS
   * group membership (only members can produce valid ciphertext).
   */
  readonly senderUserId?: UserId;
}

/**
 * A reaction targets a previously-received message by its server-
 * assigned sequence. Reactions are idempotent per (sender, target,
 * emoji) — reapplying toggles off. The empty-string emoji explicitly
 * clears a reaction (used when the UI undoes one).
 */
export interface ReactionMessage {
  readonly targetSequence: number;
  readonly emoji: string;
}

/**
 * A deletion request targets a previously-sent message by its server-
 * assigned sequence. Only honoured when the deletion envelope's
 * authenticated sender matches the original message's sender —
 * enforced client-side on receive, since MLS + the DO's sender
 * attribution together guarantee the sender field.
 */
export interface DeletionMessage {
  readonly targetSequence: number;
}

/**
 * A pin / unpin toggle on a conversation message. Any member of the
 * group can pin or unpin — pinned set lives in shared meta on every
 * device, mirroring the server-broadcast semantics of reactions.
 */
export interface PinMessage {
  readonly targetSequence: number;
  readonly pinned: boolean;
}

/**
 * Forwarded-from attribution. When present, the receiver renders a
 * "Forwarded from <name>" header on the bubble so the message isn't
 * silently attributed to whoever did the forwarding.
 */
export interface ForwardedFrom {
  readonly senderUserId: UserId;
  readonly originalAt: number;
}

/**
 * Shared group avatar update. When present, every receiver writes
 * `{ avatarMediaId, uploaderUserId }` into their local
 * ConversationMeta so the conversation header renders the group's
 * picture. Setting `avatarMediaId: null` clears it. Any current
 * member can update it (mirrors how reactions / pins work).
 */
export interface GroupAvatarUpdate {
  readonly avatarMediaId: string | null;
  readonly uploaderUserId: UserId;
}

/**
 * Shared group rename. Any current member can set or clear the
 * group's display name. `name: null` reverts every receiver back
 * to the member-derived label. Bounded length keeps a hostile
 * peer from bloating local meta.
 */
export interface GroupNameUpdate {
  readonly name: string | null;
}

/** Maximum length of a broadcast group name, in chars. */
export const GROUP_NAME_MAX_LENGTH = 80;

export interface ChatPayload {
  readonly text?: string;
  readonly attachment?: AttachmentReference;
  readonly game?: GameMessage;
  readonly reaction?: ReactionMessage;
  readonly deletion?: DeletionMessage;
  readonly pin?: PinMessage;
  readonly forwardedFrom?: ForwardedFrom;
  readonly groupAvatar?: GroupAvatarUpdate;
  readonly groupName?: GroupNameUpdate;
  /**
   * Server sequence of another message in this conversation that this
   * one is a reply to. The renderer shows a quoted preview above the
   * reply body; it's a display-only reference, not a threading model.
   */
  readonly replyToSequence?: number;
  /**
   * Ephemeral "I'm typing" marker. Senders throttle these to one
   * emit per few seconds while composing; receivers show an "X is
   * typing…" pill with a short expiry. Never persisted.
   */
  readonly typing?: true;
  /**
   * Server sequence the sender has read up through. Receivers use
   * it to render read receipts on their own messages ("seen by X").
   * Cumulative: a newer readUpTo supersedes older ones from the
   * same peer.
   */
  readonly readUpTo?: number;
}

function bucketFor(length: number): number {
  for (const bucket of PAYLOAD_BUCKETS) {
    if (length + 2 <= bucket) return bucket;
  }
  const largest = PAYLOAD_BUCKETS[PAYLOAD_BUCKETS.length - 1] ?? 65_536;
  return Math.ceil((length + 2) / largest) * largest;
}

export function encodePayload(payload: ChatPayload): Uint8Array {
  const json = new TextEncoder().encode(JSON.stringify(payload));
  if (json.length > 0xffff) {
    throw new Error('payload too large to pad (>64KiB - 2)');
  }
  const bucket = bucketFor(json.length);
  const out = new Uint8Array(bucket);
  const view = new DataView(out.buffer);
  view.setUint16(0, json.length, false);
  out.set(json, 2);
  return out;
}

export function decodePayload(bytes: Uint8Array): ChatPayload {
  if (bytes.length < 2) throw new Error('invalid chat payload');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const length = view.getUint16(0, false);
  if (2 + length > bytes.length) throw new Error('payload length exceeds block');
  const json = bytes.subarray(2, 2 + length);
  const parsed = JSON.parse(new TextDecoder().decode(json)) as unknown;
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('invalid chat payload');
  }
  const payload = parsed as {
    text?: unknown;
    attachment?: unknown;
    game?: unknown;
    reaction?: unknown;
    deletion?: unknown;
    pin?: unknown;
    forwardedFrom?: unknown;
    groupAvatar?: unknown;
    groupName?: unknown;
    replyToSequence?: unknown;
    typing?: unknown;
    readUpTo?: unknown;
  };
  const out: {
    text?: string;
    attachment?: AttachmentReference;
    game?: GameMessage;
    reaction?: ReactionMessage;
    deletion?: DeletionMessage;
    pin?: PinMessage;
    forwardedFrom?: ForwardedFrom;
    groupAvatar?: GroupAvatarUpdate;
    groupName?: GroupNameUpdate;
    replyToSequence?: number;
    typing?: true;
    readUpTo?: number;
  } = {};
  if (typeof payload.text === 'string') out.text = payload.text;
  if (payload.attachment && typeof payload.attachment === 'object') {
    // Validate every field rather than blind-cast — a hostile group
    // member could otherwise ship an envelope with non-string types
    // and crash the renderer (V5-MEDIUM-01). Drop silently on
    // mismatch, mirroring the other typed branches below.
    const a = payload.attachment as Partial<AttachmentReference>;
    if (
      typeof a.mediaId === 'string' &&
      /^[A-Za-z0-9_-]{16,64}$/.test(a.mediaId) &&
      typeof a.uploaderUserId === 'string' &&
      a.uploaderUserId.startsWith('user-') &&
      typeof a.key === 'string' &&
      typeof a.mime === 'string' &&
      a.mime.length <= 255 &&
      typeof a.filename === 'string' &&
      a.filename.length <= 255 &&
      typeof a.size === 'number' &&
      Number.isFinite(a.size) &&
      a.size >= 0
    ) {
      out.attachment = {
        mediaId: a.mediaId,
        uploaderUserId: a.uploaderUserId,
        key: a.key,
        mime: a.mime,
        filename: a.filename,
        size: a.size,
      };
    }
  }
  if (payload.game && typeof payload.game === 'object') {
    const game = payload.game as Partial<GameMessage>;
    if (
      typeof game.engineId === 'string' &&
      typeof game.moveIndex === 'number' &&
      Number.isInteger(game.moveIndex) &&
      game.moveIndex >= 0
    ) {
      const message: GameMessage = {
        engineId: game.engineId,
        moveIndex: game.moveIndex,
        move: game.move,
        ...(Array.isArray(game.players)
          ? { players: game.players.filter((p): p is UserId => typeof p === 'string') }
          : {}),
        ...(typeof game.senderUserId === 'string' ? { senderUserId: game.senderUserId } : {}),
      };
      out.game = message;
    }
  }
  if (payload.reaction && typeof payload.reaction === 'object') {
    const r = payload.reaction as Partial<ReactionMessage>;
    if (
      typeof r.targetSequence === 'number' &&
      Number.isInteger(r.targetSequence) &&
      r.targetSequence > 0 &&
      r.targetSequence <= SEQUENCE_MAX &&
      typeof r.emoji === 'string' &&
      // Cap emoji length to bound local sealed-keystore growth from a
      // hostile peer streaming reactions with random strings as keys
      // (V5-MEDIUM-02). 64 chars covers every legitimate emoji
      // including ZWJ-joined sequences with skin-tone modifiers.
      r.emoji.length <= 64
    ) {
      out.reaction = { targetSequence: r.targetSequence, emoji: r.emoji };
    }
  }
  if (payload.deletion && typeof payload.deletion === 'object') {
    const d = payload.deletion as Partial<DeletionMessage>;
    if (
      typeof d.targetSequence === 'number' &&
      Number.isInteger(d.targetSequence) &&
      d.targetSequence > 0 &&
      d.targetSequence <= SEQUENCE_MAX
    ) {
      out.deletion = { targetSequence: d.targetSequence };
    }
  }
  if (payload.pin && typeof payload.pin === 'object') {
    const p = payload.pin as Partial<PinMessage>;
    if (
      typeof p.targetSequence === 'number' &&
      Number.isInteger(p.targetSequence) &&
      p.targetSequence > 0 &&
      p.targetSequence <= SEQUENCE_MAX &&
      typeof p.pinned === 'boolean'
    ) {
      out.pin = { targetSequence: p.targetSequence, pinned: p.pinned };
    }
  }
  // V5-LOW-05: cap sequence-shaped numbers at SEQUENCE_MAX so a
  // hostile peer can't ship MAX_SAFE_INTEGER values that would
  // either be unintelligible to the renderer or interact badly with
  // future aggregation logic. The cap is well above any plausible
  // real conversation length (4 billion sequences ≈ years of
  // continuous chat).
  if (
    typeof payload.replyToSequence === 'number' &&
    Number.isInteger(payload.replyToSequence) &&
    payload.replyToSequence > 0 &&
    payload.replyToSequence <= SEQUENCE_MAX
  ) {
    out.replyToSequence = payload.replyToSequence;
  }
  if (payload.forwardedFrom && typeof payload.forwardedFrom === 'object') {
    const f = payload.forwardedFrom as Partial<ForwardedFrom>;
    if (
      typeof f.senderUserId === 'string' &&
      f.senderUserId.startsWith('user-') &&
      typeof f.originalAt === 'number' &&
      Number.isFinite(f.originalAt) &&
      f.originalAt > 0
    ) {
      out.forwardedFrom = {
        senderUserId: f.senderUserId,
        originalAt: f.originalAt,
      };
    }
  }
  if (payload.groupName && typeof payload.groupName === 'object') {
    const n = payload.groupName as Partial<GroupNameUpdate>;
    if (n.name === null || (typeof n.name === 'string' && n.name.length <= GROUP_NAME_MAX_LENGTH)) {
      out.groupName = { name: n.name };
    }
  }
  if (payload.groupAvatar && typeof payload.groupAvatar === 'object') {
    const g = payload.groupAvatar as Partial<GroupAvatarUpdate>;
    if (
      typeof g.uploaderUserId === 'string' &&
      g.uploaderUserId.startsWith('user-') &&
      (g.avatarMediaId === null ||
        (typeof g.avatarMediaId === 'string' && /^[A-Za-z0-9_-]{16,64}$/.test(g.avatarMediaId)))
    ) {
      out.groupAvatar = {
        avatarMediaId: g.avatarMediaId,
        uploaderUserId: g.uploaderUserId,
      };
    }
  }
  if (payload.typing === true) out.typing = true;
  if (
    typeof payload.readUpTo === 'number' &&
    Number.isInteger(payload.readUpTo) &&
    payload.readUpTo > 0 &&
    payload.readUpTo <= SEQUENCE_MAX
  ) {
    out.readUpTo = payload.readUpTo;
  }
  return out;
}

/** Upper bound on any payload-embedded sequence (V5-LOW-05). */
const SEQUENCE_MAX = 4_294_967_296; // 2^32
